import { Employee } from '@/api/apiEmployees';
import { ConfirmDialog } from '@/components/feature/confirm-dialog';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Table } from '@tanstack/react-table';
import { ArrowDown, ArrowUp, MoreVertical, Pencil, Trash2 } from 'lucide-react';
import { useState } from 'react';

interface EmployeeDataTableRowActionsProps {
  employee: Employee;
  table: Table<Employee>;
  onEdit: (employee: Employee) => void;
  onDelete: (empUserId: string) => void;
  onSwapPosition: (currentRow: Employee, targetRow: Employee, direction: 'up' | 'down') => void;
}

export function EmployeeDataTableRowActions({
  employee,
  table,
  onEdit,
  onDelete,
  onSwapPosition,
}: EmployeeDataTableRowActionsProps) {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Get all rows before pagination
  const allRows = table.options.data as Employee[];

  // Get current page info
  const { pageIndex, pageSize } = table.getState().pagination;

  // Find the current employee's position in the full dataset
  const currentPosition = allRows.findIndex(e => e.empUserId === employee.empUserId);

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      await onDelete(employee.empUserId);
      setShowDeleteConfirm(false);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleMove = (direction: 'up' | 'down') => {
    const targetPosition = direction === 'up' ? currentPosition - 1 : currentPosition + 1;

    // Check if the move is valid in the full dataset
    if (targetPosition >= 0 && targetPosition < allRows.length) {
      const targetRow = allRows[targetPosition];

      // Calculate if the item will still be visible on current page after swap
      const currentPageStart = pageIndex * pageSize;
      const currentPageEnd = currentPageStart + pageSize;

      // If either position is on current page, stay on current page
      const stayOnCurrentPage =
        (currentPosition >= currentPageStart && currentPosition < currentPageEnd) ||
        (targetPosition >= currentPageStart && targetPosition < currentPageEnd);

      if (!stayOnCurrentPage) {
        // If moving to a different page, calculate the target page
        const targetPage = Math.floor(targetPosition / pageSize);
        table.setPageIndex(targetPage);
      }

      onSwapPosition(employee, targetRow, direction);
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="flex h-8 w-8 p-0 data-[state=open]:bg-muted">
            <MoreVertical className="h-4 w-4" />
            <span className="sr-only">Open menu</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-[160px]">
          <DropdownMenuItem onClick={() => onEdit(employee)}>
            <Pencil className="mr-2 h-4 w-4" />
            Edit
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setShowDeleteConfirm(true)} className="text-destructive">
            <Trash2 className="mr-2 h-4 w-4" />
            Delete
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handleMove('up')} disabled={currentPosition === 0}>
            <ArrowUp className="mr-2 h-4 w-4" />
            Move Up
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => handleMove('down')}
            disabled={currentPosition === allRows.length - 1}
          >
            <ArrowDown className="mr-2 h-4 w-4" />
            Move Down
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <ConfirmDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        title="Delete Employee"
        description={`Are you sure you want to delete "${employee.empName}" ? This action cannot be undone.`}
        confirmText="Delete"
        confirmVariant="destructive"
        onConfirm={handleDelete}
        isLoading={isDeleting}
      />
    </>
  );
}
